/**
 * PeptidesPage.tsx
 *
 * Account-wide Peptides view: every Axiom peptide order across all patients,
 * in the same list chrome as Patients/Memberships (ListPageLayout + filter
 * sidebar + KPI tiles). The order table is the shared IntegrationOrdersSubTab
 * fed account-wide data + a Patient column. Re-order navigates to that patient's
 * Peptides tab, where the full ordering context (prescriber, shipping, payment)
 * lives.
 *
 * ── Two product lines, two flows ────────────────────────────────────────────
 * Pens ('axiom') and vials ('axiom_vials') are separate Axiom-family
 * integrations with separate catalogs, separate enablement and separate
 * onboarding. useAxiomOrderFlow fixes its integration key at construction —
 * startNewOrder() takes only a patient — so the page runs TWO flows and keeps
 * everything downstream of them paired: the order buttons, the account query
 * key each flow invalidates on refresh, the row actions handed to each tab's
 * table, and the modals rendered at the page root. Handing one tab the other
 * line's handlers would cancel against the wrong integration key and re-order
 * into a catalog that does not contain the order's products.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LifeBuoy, Megaphone, Syringe } from 'lucide-react';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { useAuth } from '@/hooks/useAuth';
import { useIntegrationOrderFilterOptions } from '@/hooks/filters';
import { buildApiParams, FILTER_KEYS } from '@/lib/filterRegistry';
import { useFacilityOverrideValues, useLocalDashboardFilters } from '@/pages/dashboard/dashboardFilters';
import {
  AWAITING_PAYMENT_STATUSES,
  AXIOM_IN_FLIGHT_STATUSES,
  AXIOM_TILE_STATUSES,
  axiomOrderKpis,
  INTEGRATION_ORDER_FILTER_CONFIGS,
  integrationOrderMarketingConditions,
  marketingListBlockedReason,
  NO_FACILITY,
  selectEquals,
} from '@/pages/marketplace/integrationOrdersShared';
import { MarketingActionsModal } from '@/components/marketing/MarketingActionsModal';
import { IntegrationOrdersSubTab } from '@/pages/patients/labs/IntegrationOrdersSubTab';
import { useAxiomOrderFlow, type AxiomOrderFlow } from '@/pages/patients/labs/useAxiomOrderFlow';
import { OrderPatientPicker } from '@/components/orders/OrderPatientPicker';
import type { PaidReference } from '@/pages/patients/labs/AxiomOrderModal';
import {
  getAccountIntegrationOrders,
  type AxiomAccountTicket,
  type IntegrationOrder,
} from '@/services/integrations.service';
import { listFacilities } from '@/services/admin.service';
import type { PatientSummary } from '@/types/patient.types';
import { AXIOM_FULFILLMENT_LABELS, AXIOM_STATUS_HELP, axiomFulfillmentLabels } from '@/pages/patients/PeptidesTab';
import { AxiomAllTicketsSheet } from './AxiomAllTicketsSheet';
import { isDueForReorder, computeSupersededBy } from '@/lib/peptideOrder';
import { isReadyForPickup } from '@/lib/orderHandoff';

/** The two Axiom-family product lines this page orders and lists. */
type ProductLine = 'pens' | 'vials';
/** What a new order is for: one line, or the combined pens + vials cart. */
type OrderLine = ProductLine | 'combined';

const PICKER_TITLES: Record<OrderLine, string> = {
  pens: 'New Peptide Pens Order',
  vials: 'New Peptide Vials Order',
  combined: 'New Peptides Order',
};

/** Peptides' patient picker: the shared one, titled with the product line. */
function NewOrderPatientPicker({ line, onClose, onSelect }: {
  line: OrderLine | null;
  onClose: () => void;
  onSelect: (patient: PatientSummary) => void;
}) {
  return (
    <OrderPatientPicker
      isOpen={line !== null}
      title={PICKER_TITLES[line ?? 'pens']}
      onClose={onClose}
      onSelect={onSelect}
    />
  );
}

/**
 * Filters: the shared Marketplace order configs (canonical keys), resolved by
 * the API's `integration_order` pipeline — the same one a contact list saved
 * from this page uses, so the two always agree (SCRUM-2207). The KPI tiles
 * set the Status filter rather than filtering on their own.
 */
const AWAITING_PAYMENT = AWAITING_PAYMENT_STATUSES;

/**
 * Row actions bound to ONE flow. Built per product line so the vials tab cancels
 * against 'axiom_vials' and re-orders into the vials catalog — a shared,
 * pens-bound set would 422 on both.
 */
function rowActionsFor(flow: AxiomOrderFlow) {
  return {
    onReorder: (o: IntegrationOrder) => { if (o.patientId) flow.reorder(o, o.patientId); },
    onEdit: (o: IntegrationOrder) => { if (o.patientId) flow.edit(o, o.patientId); },
    onClone: (o: IntegrationOrder) => { if (o.patientId) flow.clone(o, o.patientId); },
    onResend: (o: IntegrationOrder) => { if (o.patientId) flow.resend(o, o.patientId); },
    onFinishPayment: (o: IntegrationOrder) => { if (o.patientId) flow.finishPayment(o, o.patientId); },
    onCancel: (o: IntegrationOrder) => { if (o.patientId) void flow.cancel(o, o.patientId); },
    onRetry: (o: IntegrationOrder) => { if (o.patientId) void flow.retry(o, o.patientId); },
  };
}

export default function PeptidesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const filterState = useLocalDashboardFilters(INTEGRATION_ORDER_FILTER_CONFIGS, 'peptides');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);
  /**
   * Under a global facility override the override wins — plus "No facility":
   * a locked control cannot be cleared, and orders whose facility could not
   * be resolved would otherwise disappear from the account with no way back.
   */
  const pageValues = useMemo(() => {
    if (!isFacilityLocked) return effectiveValues;
    const ids = Array.isArray(effectiveValues.facility) ? effectiveValues.facility : [];
    return { ...effectiveValues, [FILTER_KEYS.FACILITY]: [...ids, NO_FACILITY] };
  }, [effectiveValues, isFacilityLocked]);
  const [marketingModalOpen, setMarketingModalOpen] = useState(false);

  const { data: pensOrders = [], isLoading: pensLoading } = useQuery({
    queryKey: ['account-axiom-orders'],
    queryFn: () => getAccountIntegrationOrders('axiom'),
    staleTime: 30_000,
  });
  const { data: vialsOrders = [], isLoading: vialsLoading } = useQuery({
    queryKey: ['account-axiom_vials-orders'],
    queryFn: () => getAccountIntegrationOrders('axiom_vials'),
    staleTime: 30_000,
  });

  /**
   * Orders with an Axiom ticket update nobody has opened yet (the ticket
   * webhook sets metadata.supportAlert; opening the ticket clears it). Shown
   * as a red count on "View All Tickets" (SCRUM-1994).
   */
  const unreadTicketOrders = useMemo(
    () => [...pensOrders, ...vialsOrders].filter(
      (o) => !!(o.metadata as { supportAlert?: unknown } | null | undefined)?.supportAlert,
    ).length,
    [pensOrders, vialsOrders],
  );

  // Shared order + payment orchestration (same modals as the patient tab), one
  // per line. Each invalidates ITS OWN account query key on refresh, so a newly
  // placed order shows up in the tab it belongs to without a reload.
  //
  // The pens flow also owns the combined pens + vials cart for NEW orders when
  // both lines are enabled (combineVials) — it can place a vials order, so it
  // refreshes the vials list too. Row actions stay per line.
  const pensFlow = useAxiomOrderFlow({
    combineVials: true,
    onRefresh: () => {
      void queryClient.invalidateQueries({ queryKey: ['account-axiom-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['account-integration-orders', 'axiom'] });
      void queryClient.invalidateQueries({ queryKey: ['account-axiom_vials-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['account-integration-orders', 'axiom_vials'] });
    },
  });
  const vialsFlow = useAxiomOrderFlow({
    integrationKey: 'axiom_vials',
    onRefresh: () => {
      void queryClient.invalidateQueries({ queryKey: ['account-axiom_vials-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['account-integration-orders', 'axiom_vials'] });
    },
  });
  /**
   * Arrival from App Orders' Fulfill action: `?fulfillPatient=<id>` opens a new
   * Axiom order for that patient, and router state carries what they paid for,
   * shown read-only beside the catalog.
   *
   * Runs once per arrival — the ref guards against re-opening the modal after
   * the user closes it, which the effect would otherwise do on every render
   * while the query param is still in the URL. The param is then stripped so a
   * refresh or a back-navigation does not re-trigger it.
   *
   * Pens, not vials: the payment says a peptide was bought, not which Axiom
   * line fills it, and pens is this page's default. Staff switch tabs if it was
   * vials — a guess either way, made visible rather than hidden.
   */
  const fulfilledRef = useRef<string | null>(null);
  useEffect(() => {
    const raw = searchParams.get('fulfillPatient');
    if (!raw || fulfilledRef.current === raw) return;
    const patientId = Number(raw);
    if (!patientId) return;
    // Wait until the flow knows whether ordering is available — the
    // integration AND its onboarding status. Waiting on the integration alone
    // opened the order while onboarding was still loading (read as "not
    // onboarded"), so an onboarded practice got "ordering is locked until your
    // practice completes Axiom onboarding" — SUP-000744's error on Fulfill.
    if (!pensFlow.axiom || pensFlow.onboardingKnown === false) return;

    fulfilledRef.current = raw;
    const paid = (location.state as { fulfillPayment?: PaidReference } | null)?.fulfillPayment ?? null;
    pensFlow.startNewOrder(patientId, paid);
    navigate('/peptides', { replace: true });
  }, [searchParams, location.state, pensFlow, navigate]);

  const pensEnabled = !!pensFlow.axiom;
  const vialsEnabled = !!vialsFlow.axiom;
  const anyEnabled = pensEnabled || vialsEnabled;
  const bothEnabled = pensEnabled && vialsEnabled;
  // One "Order Peptides" button (combined cart, no line step) when both lines
  // are on. Falls back to the per-line buttons only when vials is usable but
  // pens is not onboarded — the combined cart rides on the pens onboarding.
  const combinedOrder = pensFlow.combined && !(vialsFlow.onboarded && !pensFlow.onboarded);

  // Tab bar only appears with both lines enabled; with one, its tab is forced.
  const [activeTab, setActiveTab] = useState<ProductLine>('pens');

  /**
   * Arrival from an Axiom support alert: `?ticketOrder=<orderId>` switches to
   * that order's line and opens its ticket (SCRUM-1994 — the alert used to
   * land on the patient's details page). Once both lists have loaded; the
   * param is then stripped so a refresh does not reopen it.
   */
  const [ticketOrder, setTicketOrder] = useState<{ order: IntegrationOrder; ticketId: number | null } | null>(null);
  const ticketParam = Number(searchParams.get('ticketOrder')) || 0;
  useEffect(() => {
    if (!ticketParam || pensLoading || vialsLoading) return;
    const inPens = pensOrders.find((o) => o.integrationOrderId === ticketParam);
    const inVials = vialsOrders.find((o) => o.integrationOrderId === ticketParam);
    const found = inPens ?? inVials ?? null;
    if (found) {
      setActiveTab(inPens ? 'pens' : 'vials');
      setTicketOrder({ order: found, ticketId: null });
    }
    navigate('/peptides', { replace: true });
  }, [ticketParam, pensLoading, vialsLoading, pensOrders, vialsOrders, navigate]);
  const clearTicketOrder = useCallback(() => setTicketOrder(null), []);
  const activeLine: ProductLine = bothEnabled ? activeTab : (vialsEnabled && !pensEnabled ? 'vials' : 'pens');
  const activeKey = activeLine === 'vials' ? 'axiom_vials' : 'axiom';
  // Every order of the line: the KPI tiles' population and the ticket lookup.
  const activeOrders = activeLine === 'vials' ? vialsOrders : pensOrders;

  // The table: the line's orders through the page's filters, server-side.
  const apiParams = useMemo(
    () => buildApiParams(pageValues, filterState.negations),
    [pageValues, filterState.negations],
  );
  const { data: filtered = [], isLoading: activeLoading } = useQuery({
    queryKey: ['account-integration-orders', activeKey, apiParams],
    queryFn: () => getAccountIntegrationOrders(activeKey, apiParams),
    staleTime: 30_000,
    // Keep the rows on screen while a filter change refetches — but never
    // another line's rows when the tab changes.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === activeKey ? prev : undefined),
    enabled: pensFlow.axiom != null || vialsFlow.axiom != null,
  });

  // Order buttons: pick the line first (it decides which flow receives the
  // patient), then the patient. Non-null = picker open, and which line opened it.
  // 'combined' = the single Peptides button: straight to the patient, then the
  // pens flow's combined cart.
  const [pickerLine, setPickerLine] = useState<OrderLine | null>(null);
  const [allTicketsOpen, setAllTicketsOpen] = useState(false);
  const handlePickPatient = useCallback((p: { patientId: number }) => {
    const line = pickerLine;
    setPickerLine(null);
    if (line === 'vials') vialsFlow.startNewOrder(p.patientId);
    else pensFlow.startNewOrder(p.patientId);
  }, [pickerLine, pensFlow, vialsFlow]);

  // ── Facility (labels the export's Facility column) ─────────────────────
  const { data: facilities = [] } = useQuery({
    queryKey: ['admin-facilities'],
    queryFn: listFacilities,
    staleTime: 5 * 60_000,
  });
  const facilityName = useCallback((id: number | null | undefined) => {
    if (id == null) return '';
    return facilities.find((f) => f.facilityId === id)?.facilityName ?? '';
  }, [facilities]);
  const exportAs = useMemo(
    () => ({ filePrefix: activeLine === 'vials' ? 'axiom-vials' : 'axiom-pens', facilityName }),
    [activeLine, facilityName],
  );

  const fulfillmentLabels = useMemo(
    () => axiomFulfillmentLabels((activeLine === 'vials' ? vialsFlow : pensFlow).axiom?.submitDelayMinutes),
    [activeLine, pensFlow, vialsFlow],
  );
  const sidebarOptions = useIntegrationOrderFilterOptions({ integrationKey: activeKey, statusLabels: AXIOM_FULFILLMENT_LABELS, ensureStatuses: AXIOM_TILE_STATUSES });

  /**
   * KPI tiles count every order of the active line within its facilities (the
   * structural scope); the rail's other filters narrow the table, not them.
   * Awaiting Payment and In Fulfillment set the Status filter; Due for
   * Re-order is a count (it is computed from supply, not a stored status).
   */
  const facilityScope = Array.isArray(pageValues.facility) ? (pageValues.facility as string[]) : [];
  const facilityNegated = filterState.negations[FILTER_KEYS.FACILITY] === true;
  // By facility id, so the "All tickets" sheet applies the same filter.
  const facilityIdMatches = useCallback((facilityId: number | null | undefined) => {
    if (facilityScope.length === 0) return true;
    const inScope = facilityScope.includes(facilityId == null || facilityId === 0 ? NO_FACILITY : String(facilityId));
    // "Not facility X": the tiles count what the table lists.
    return facilityNegated ? !inScope : inScope;
  }, [facilityScope, facilityNegated]);
  const matchesFacility = useCallback(
    (o: IntegrationOrder) => facilityIdMatches(o.facilityId),
    [facilityIdMatches],
  );
  const supersededBy = useMemo(
    () => computeSupersededBy(activeOrders, (o) => o.patientId ?? 0),
    [activeOrders],
  );
  const kpis = useMemo(() => axiomOrderKpis(activeOrders, matchesFacility), [activeOrders, matchesFacility]);
  // Due for Re-order narrows the table on the page (it is computed from
  // supply, not a stored status); a list cannot keep it, so the button says so.
  const [reorderOnly, setReorderOnly] = useState(false);
  // Ready for pickup (SCRUM-2200) works the same way: it is derived from the
  // ship-to and the clinic's handoff stamps, not a stored status, so it
  // narrows the table here and a list cannot keep it. One derived tile at a
  // time — the two together would be an intersection nobody asked for.
  const [pickupOnly, setPickupOnly] = useState(false);
  const tableOrders = useMemo(() => {
    if (reorderOnly) return filtered.filter((o) => isDueForReorder(o, supersededBy));
    if (pickupOnly) return filtered.filter(isReadyForPickup);
    return filtered;
  }, [filtered, reorderOnly, pickupOnly, supersededBy]);
  const listBlockedReason = marketingListBlockedReason(pageValues, { reorderDue: reorderOnly, readyForPickup: pickupOnly });
  const statusFilter = pageValues[FILTER_KEYS.ORDER_STATUS];
  const toggleStatusTile = useCallback((statuses: string[]) => {
    if (selectEquals(filterState.values[FILTER_KEYS.ORDER_STATUS], statuses)) filterState.clearFilter(FILTER_KEYS.ORDER_STATUS);
    else filterState.setFilter(FILTER_KEYS.ORDER_STATUS, statuses);
  }, [filterState]);

  const hasActiveFilters = reorderOnly || pickupOnly || Object.entries(pageValues).some(([key, v]) => {
    if (isFacilityLocked && key === FILTER_KEYS.FACILITY) return false;
    if (Array.isArray(v)) return v.some((x) => String(x).trim() !== '');
    return !!(v && typeof v === 'object' && 'from' in v && (v.from || v.to));
  });
  const emptyMessage = activeLine === 'vials'
    ? (hasActiveFilters ? 'No vial orders match your filters.' : 'No vial orders yet.')
    : (hasActiveFilters ? 'No pen orders match your filters.' : 'No pen orders yet.');

  const pensActions = useMemo(() => rowActionsFor(pensFlow), [pensFlow]);
  const vialsActions = useMemo(() => rowActionsFor(vialsFlow), [vialsFlow]);
  const rowActions = activeLine === 'vials' ? vialsActions : pensActions;

  // A ticket from "All tickets": switch to its order's line and open that
  // ticket's thread here, as the ?ticketOrder= alert link does. Held until both
  // order lists have loaded (the same guard as that effect): before then every
  // order looks missing. An order neither list has after loading, e.g. one the
  // account's order query no longer returns, opens on the patient's chart.
  const [pendingTicket, setPendingTicket] = useState<AxiomAccountTicket | null>(null);
  const handleOpenTicket = useCallback((t: AxiomAccountTicket) => {
    setAllTicketsOpen(false);
    void queryClient.invalidateQueries({ queryKey: ['axiom-account-tickets'] });
    setPendingTicket(t);
  }, [queryClient]);
  useEffect(() => {
    if (!pendingTicket || pensLoading || vialsLoading) return;
    const t = pendingTicket;
    setPendingTicket(null);
    const inPens = pensOrders.find((o) => o.integrationOrderId === t.integrationOrderId);
    const inVials = vialsOrders.find((o) => o.integrationOrderId === t.integrationOrderId);
    const found = inPens ?? inVials;
    if (found) {
      setActiveTab(inPens ? 'pens' : 'vials');
      setTicketOrder({ order: found, ticketId: t.axiomSupportTicketId });
    } else if (t.patientId > 0) {
      navigate(`/patients/${t.patientId}?section=peptides`);
    }
  }, [pendingTicket, pensLoading, vialsLoading, pensOrders, vialsOrders, navigate]);

  // Account-level support: reachable as soon as EITHER line is enabled and
  // onboarded — a vials-only account must not lose it.
  const supportOnboarded = (pensEnabled && pensFlow.onboarded) || (vialsEnabled && vialsFlow.onboarded);

  return (
    <ListPageLayout
      filterSidebar={(
        <FilterSidebar
          filters={INTEGRATION_ORDER_FILTER_CONFIGS}
          values={effectiveValues}
          options={sidebarOptions}
          negations={filterState.negations}
          onChange={filterState.setFilter}
          onNegateChange={filterState.setNegation}
          onClear={filterState.clearFilter}
          lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          searchCommitMode="blur-or-enter"
          showAIAssistant={false}
          storageKey="peptides"
        />
      )}
    >
      <div className="flex h-full min-h-0 flex-col max-md:h-auto">
        <div className="shrink-0 space-y-6 p-6">
          <PageHeader
            variant="compact"
            title="Peptides"
            subtitle="All Axiom peptide orders across your patients"
            actions={anyEnabled && (
              <div className="flex items-center gap-2">
                {hasPermission(20) && (
                  <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<Megaphone className="h-4 w-4" />}
                    disabled={listBlockedReason !== null}
                    title={listBlockedReason ?? undefined}
                    onClick={() => setMarketingModalOpen(true)}
                  >
                    Email / SMS List
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!supportOnboarded}
                  title={supportOnboarded ? undefined : 'Your practice must complete Axiom onboarding (Admin → Integrations → Axiom) before contacting support.'}
                  leftIcon={<LifeBuoy className="h-4 w-4" />}
                  onClick={() => setAllTicketsOpen(true)}
                >
                  View All Tickets
                  {unreadTicketOrders > 0 && (
                    <span
                      data-testid="tickets-unread-badge"
                      title={`${unreadTicketOrders} order${unreadTicketOrders === 1 ? ' has' : 's have'} a new reply from Axiom — open the order's support (lifebuoy) to read it`}
                      className="ml-1.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-semibold leading-[18px] text-white"
                    >
                      {unreadTicketOrders}
                    </span>
                  )}
                </Button>
                {combinedOrder && (
                  <Button
                    size="sm"
                    disabled={!pensFlow.onboarded}
                    title={pensFlow.onboarded ? undefined : 'Your practice must complete Axiom onboarding (Admin → Integrations → Axiom) before ordering.'}
                    leftIcon={<Syringe className="h-4 w-4" />}
                    onClick={() => setPickerLine('combined')}
                  >
                    Order Peptides
                  </Button>
                )}
                {pensEnabled && !combinedOrder && (
                  <Button
                    size="sm"
                    disabled={!pensFlow.onboarded}
                    title={pensFlow.onboarded ? undefined : 'Your practice must complete Axiom onboarding (Admin → Integrations → Axiom) before ordering.'}
                    leftIcon={<Syringe className="h-4 w-4" />}
                    onClick={() => setPickerLine('pens')}
                  >
                    Order Peptide Pens
                  </Button>
                )}
                {vialsEnabled && !combinedOrder && (
                  <Button
                    size="sm"
                    disabled={!vialsFlow.onboarded}
                    title={vialsFlow.onboarded ? undefined : 'Your practice must complete Axiom onboarding (Admin → Integrations → Axiom) before ordering.'}
                    leftIcon={<Syringe className="h-4 w-4" />}
                    onClick={() => setPickerLine('vials')}
                  >
                    Order Peptide Vials
                  </Button>
                )}
              </div>
            )}
          />

          {anyEnabled && (
            <MetricCardGrid columns={5}>
              {/* Total Orders clears the Status filter and the derived tiles; the next two set Status. */}
              <StatCard label="Total Orders" value={kpis.total} tone="blue" onClick={() => { filterState.clearFilter(FILTER_KEYS.ORDER_STATUS); setReorderOnly(false); setPickupOnly(false); }} tooltip="Show all statuses" />
              <StatCard label="Awaiting Payment" value={kpis.awaitingPayment} tone={kpis.awaitingPayment > 0 ? 'amber' : 'slate'} onClick={() => toggleStatusTile(AWAITING_PAYMENT)} selected={selectEquals(statusFilter, AWAITING_PAYMENT)} />
              <StatCard label="In Fulfillment" value={kpis.inFlight} tone="violet" onClick={() => toggleStatusTile(AXIOM_IN_FLIGHT_STATUSES)} selected={selectEquals(statusFilter, AXIOM_IN_FLIGHT_STATUSES)} />
              <StatCard label="Due for Re-order" value={kpis.reorderDue} tone={kpis.reorderDue > 0 ? 'amber' : 'slate'} onClick={() => { setPickupOnly(false); setReorderOnly((on) => !on); }} selected={reorderOnly} tooltip="Orders whose supply runs out soon" />
              <StatCard label="Ready for pickup" value={kpis.readyForPickup} tone={kpis.readyForPickup > 0 ? 'green' : 'slate'} onClick={() => { setReorderOnly(false); setPickupOnly((on) => !on); }} selected={pickupOnly} tooltip="Delivered to the clinic and not picked up yet" />
            </MetricCardGrid>
          )}

          {bothEnabled && (
            <div className="flex items-center gap-1 border-b border-slate-200">
              {([
                { key: 'pens', label: 'Pen Orders' },
                { key: 'vials', label: 'Vial Orders' },
              ] as const).map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors ${
                    activeLine === tab.key
                      ? 'border-b-2 border-blue-600 text-blue-700'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-6 pb-6 max-md:overflow-visible">
          {!anyEnabled ? (
            <p className="py-8 text-center text-sm text-slate-500">
              Peptide ordering is not enabled for this practice. Please contact your practice administrator to
              add this feature to your account.
            </p>
          ) : activeLoading ? (
            <p className="py-8 text-center text-sm text-slate-500">Loading peptide orders…</p>
          ) : (
            <IntegrationOrdersSubTab
              // Keyed by line so switching tabs remounts the table rather than
              // carrying the other line's sort/show-cancelled state across.
              key={activeLine}
              patientId={0}
              integrationKey={activeKey}
              orders={tableOrders}
              allOrders={activeOrders}
              showPatient
              openSupportFor={ticketOrder?.order ?? null}
              openSupportTicketId={ticketOrder?.ticketId ?? null}
              onSupportOpened={clearTicketOrder}
              exportAs={exportAs}
              fulfillmentLabels={fulfillmentLabels}
              statusHelp={AXIOM_STATUS_HELP}
              emptyMessage={emptyMessage}
              onCancel={rowActions.onCancel}
              onFinishPayment={rowActions.onFinishPayment}
              onEdit={rowActions.onEdit}
              onClone={rowActions.onClone}
              onResend={rowActions.onResend}
              onReorder={rowActions.onReorder}
              onRetry={rowActions.onRetry}
            />
          )}
        </div>
      </div>
      <NewOrderPatientPicker
        line={pickerLine}
        onClose={() => setPickerLine(null)}
        onSelect={handlePickPatient}
      />
      <AxiomAllTicketsSheet
        open={allTicketsOpen}
        onClose={() => setAllTicketsOpen(false)}
        onOpenTicket={handleOpenTicket}
        matchesFacility={facilityIdMatches}
      />
      {hasPermission(20) && marketingModalOpen && (
        <MarketingActionsModal
          isOpen
          onClose={() => setMarketingModalOpen(false)}
          sourceType="integration_order"
          listLabel={activeLine === 'vials' ? 'Peptide Vials' : 'Peptide Pens'}
          filterConditions={integrationOrderMarketingConditions(activeKey, pageValues, filterState.negations)}
        />
      )}
      {pensFlow.modals}
      {vialsFlow.modals}
    </ListPageLayout>
  );
}
